import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Close16,
  CheckmarkFilled16,
  CloudDownload16,
  DocumentPdf16
} from '@carbon/icons-react';
import {
  Loading,
  Tag,
  DatePicker,
  Button,
  TooltipDefinition
} from 'carbon-components-react';
import {
  toLocalDateFormat,
  downloadFile,
  formFullAddress
} from 'utilities/helper.util';
import { useComponentMount, useEventListener } from 'utilities/hooks.util';
import { selectorAlertNotificationShowHide } from 'redux/session/selectors';
import { addAlert } from 'redux/session/actions';
import { useSelector, useDispatch } from 'react-redux';
import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import {
  getEnquiryDetailsStart,
  downloadCombinedPDFStart,
  downloadCombinedZIPStart
} from './localStore/actions';

const LoadingBar = (): React.ReactElement => (
  <span className="loading-bar">
    <Loading small withOverlay={false} />
  </span>
);

const EnquiryDetailsExpired: React.FC<Props> = ({
  handleCloseIconClick,
  enquiryIdDetails,
  isAnonymousView = false,
  anonymousEnquiryDetailsData,
  vhToken,
  plToken
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<DatePicker>(null);
  const alertNotificationToggle = useSelector(
    selectorAlertNotificationShowHide
  );
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const {
    enquiryDetails,
    isLoadingEnquiryDetails,
    combinedPDFDownloadURL,
    isDownloadingCombinedPDF,
    combinedZIPDownloadData,
    isDownloadingCombinedZIP
  } = localStore;

  const [filteredEnquiryDetails, setFilteredEnquiryDetails] = useState<any>('');
  const [enquiryDetailsData, setEnquiryDetailsData] = useState<any>('');
  const [downloadZIPIconState, setDownloadZIPIconState] = useState('default');
  const [downloadPDFIconState, setDownloadPDFIconState] = useState('default');

  const handleClickOutside = useCallback(
    (evt: any) => {
      const systemToastAlert = document
        .getElementsByClassName('system-alert')
        .item(0);
      const previewer = document
        .getElementsByClassName('previewer-modal')
        .item(0);
      const excludeFilesModal = document
        .getElementsByClassName('exclude-files-modal')
        .item(0);
      if (containerRef.current && evt.target) {
        if (
          !containerRef.current.contains(evt.target) &&
          !calendarRef.current &&
          !previewer &&
          !systemToastAlert &&
          !excludeFilesModal
        ) {
          !isAnonymousView && handleCloseIconClick();
        }
      }
    },
    [containerRef, handleCloseIconClick, isAnonymousView]
  );

  const handleDownloadPDF = () => {
    const params = {
      enquiryId: enquiryIdDetails || filteredEnquiryDetails.id,
      isAnonymousView,
      plToken: isAnonymousView ? plToken : undefined,
      vhToken: isAnonymousView ? vhToken : undefined
    };
    setDownloadPDFIconState('loading');
    localDispatch(downloadCombinedPDFStart(params));
  };

  const handleDownloadZIP = () => {
    const params = {
      enquiryId: enquiryIdDetails || filteredEnquiryDetails.id,
      isAnonymousView,
      plToken: isAnonymousView ? plToken : undefined,
      vhToken: isAnonymousView ? vhToken : undefined
    };
    setDownloadZIPIconState('loading');
    localDispatch(downloadCombinedZIPStart(params));
  };

  const downloadZipIcon = () => {
    switch (downloadZIPIconState) {
      case 'loading':
        return <LoadingBar />;
      case 'completed':
        return <CheckmarkFilled16 className="icon-green" />;
      default:
        return <CloudDownload16 />;
    }
  };

  const downloadPDFIcon = () => {
    switch (downloadPDFIconState) {
      case 'loading':
        return <LoadingBar />;
      case 'completed':
        return <CheckmarkFilled16 className="icon-green" />;
      default:
        return <DocumentPdf16 />;
    }
  };

  useEffect(() => {
    if (!isDownloadingCombinedZIP && combinedZIPDownloadData) {
      const { blobData, fileName } = combinedZIPDownloadData;
      downloadFile(blobData, fileName, 'application/zip');
      setDownloadZIPIconState('completed');
      setTimeout(() => {
        setDownloadZIPIconState('default');
      }, 2000);
    }
  }, [isDownloadingCombinedZIP, combinedZIPDownloadData]);

  useEffect(() => {
    if (!isDownloadingCombinedPDF && combinedPDFDownloadURL) {
      window.location.href = combinedPDFDownloadURL;
      setDownloadPDFIconState('completed');
      setTimeout(() => {
        setDownloadPDFIconState('default');
      }, 2000);
    }
  }, [isDownloadingCombinedPDF, combinedPDFDownloadURL]);

  useEffect(() => {
    if (isAnonymousView && anonymousEnquiryDetailsData) {
      setEnquiryDetailsData(anonymousEnquiryDetailsData);
    } else if (!isAnonymousView && enquiryDetails) {
      if (typeof enquiryDetails === 'string') {
        handleCloseIconClick();
        dispatch(addAlert('error', enquiryDetails));
      } else {
        setEnquiryDetailsData(enquiryDetails);
      }
    }
  }, [
    anonymousEnquiryDetailsData,
    enquiryDetails,
    isAnonymousView,
    handleCloseIconClick,
    dispatch
  ]);

  useEffect(() => {
    if (!isLoadingEnquiryDetails && enquiryDetailsData) {
      const isExpired = enquiryDetailsData.is_expired;
      const archivedAt = enquiryDetailsData.archived_at;
      const status = isExpired
        ? 'expired'
        : archivedAt
        ? 'archived'
        : enquiryDetailsData.status;
      const address = formFullAddress(enquiryDetailsData.address);
      const filteredEnquiryDetails = {
        ...enquiryDetailsData,
        status,
        address
      };
      setFilteredEnquiryDetails(filteredEnquiryDetails);
    }
  }, [isLoadingEnquiryDetails, enquiryDetailsData]);

  useComponentMount(() => {
    const params = { enquiryId: enquiryIdDetails };
    !isAnonymousView && localDispatch(getEnquiryDetailsStart(params));
  });

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  return (
    <>
      <div
        className={`${
          alertNotificationToggle ? 'noti-alert-notification-rc-modal' : ''
        } enquiry-details-rc-modal-expired`}
      >
        <section
          ref={containerRef}
          className={`enquiry-details-rc-modal-main ${
            isAnonymousView ? 'anonymous-view' : ''
          }`}
        >
          <div className="close-button-container">
            {!isAnonymousView && (
              <div
                className="close-button-body"
                onClick={() => handleCloseIconClick()}
              >
                <Close16 />
              </div>
            )}
          </div>
          {enquiryIdDetails !== 0 &&
            !isLoadingEnquiryDetails &&
            filteredEnquiryDetails && (
              <div className="modal-container">
                <div className="modal-header-container">
                  <div className="modal-header-container-top">
                    <div className="modal-header-container-top-left">
                      <Tag
                        type="purple"
                        // className={`modal-title-tag ${filteredEnquiryDetails.status.toLowerCase()}`}
                        className={`modal-title-tag expired`}
                      >
                        {`Job ${filteredEnquiryDetails.external_id}`}
                      </Tag>
                      <div className="expiry-date">
                        Expired at{' '}
                        {toLocalDateFormat(filteredEnquiryDetails.expire_at)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`modal-content-container`}>
                  <div className="text-wrapper">
                    <p>
                      This enquiry&apos;s responses can no longer be viewed
                      here.
                      <br />
                      {!isAnonymousView && <br />}
                      Responses are automatically deleted 90 days after the date
                      they were received. You can still download the responses.
                    </p>
                  </div>
                  <div className="download-wrapper">
                    <TooltipDefinition
                      tooltipText="Download all responses in a single ZIP file"
                      direction="bottom"
                      align="center"
                      className="response-expired-tooltip"
                      style={{ marginBottom: '1rem' }}
                    >
                      <Button
                        className="main_cont"
                        kind={'secondary'}
                        renderIcon={downloadZipIcon}
                        onClick={handleDownloadZIP}
                        disabled={isDownloadingCombinedZIP}
                      >
                        <div className="get-plans-actions-downloads-text">
                          Download as ZIP
                        </div>
                      </Button>
                    </TooltipDefinition>
                    <br />
                    <TooltipDefinition
                      tooltipText="Download all responses in a single PDF document. Some non-printable files may not be included in the output."
                      direction="bottom"
                      align="center"
                      className="response-expired-tooltip"
                      style={{ marginBottom: '1rem' }}
                    >
                      <Button
                        className="main_cont"
                        kind={'secondary'}
                        renderIcon={downloadPDFIcon}
                        onClick={handleDownloadPDF}
                        disabled={isDownloadingCombinedPDF}
                      >
                        <div className="get-plans-actions-downloads-text">
                          Download as PDF
                        </div>
                      </Button>
                    </TooltipDefinition>
                  </div>
                </div>
              </div>
            )}
          {(enquiryIdDetails === 0 || isLoadingEnquiryDetails) && (
            <div className="loading-details">
              <Loading withOverlay={true} />
            </div>
          )}
        </section>
      </div>
    </>
  );
};

EnquiryDetailsExpired.displayName = 'EnquiryDetailsExpired';

type Props = {
  handleCloseIconClick?: any;
  enquiryIdDetails?: number;
  isAnonymousView?: boolean;
  anonymousEnquiryDetailsData?: any;
  vhToken?: string;
  plToken?: string;
};

export default React.memo(EnquiryDetailsExpired);
